import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, Star, ShieldCheck, Users, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";
import CategoryCard from "../components/CategoryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const Landing = () => {
  const [categories, setCategories] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        // Check if user is logged in
        const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(authRes.data);
      } catch (error) {
        // Not logged in, that's fine
      }

      try {
        // Seed categories if needed
        await axios.post(`${API}/seed`);

        // Fetch categories and featured services
        const [catRes, svcRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/services?featured=true`),
        ]);

        setCategories(catRes.data);
        setFeaturedServices(svcRes.data.slice(0, 6));
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/directory?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="landing-page">
      <Header user={user} transparent />

      {/* Hero Section */}
      <section className="pt-24 lg:pt-32 pb-16 lg:pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="animate-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight mb-6">
                Build Your
                <span className="text-[#1A4D2E]"> Subcontractor </span>
                Network
              </h1>
              <p className="text-lg text-[#4A4A4A] mb-8 max-w-lg">
                The go-to platform for contractors and property managers. 
                Find, vet, and manage reliable subcontractors across 20+ trade categories.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="mb-8">
                <div className="flex gap-2 p-2 bg-white rounded-2xl shadow-lg border border-stone-200">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                    <Input
                      type="text"
                      placeholder="Search trades: plumber, electrician, roofer..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 h-12 border-0 bg-transparent focus-visible:ring-0 text-[#1A1A1A]"
                      data-testid="hero-search-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-xl px-8 h-12 btn-hover-lift"
                    data-testid="hero-search-btn"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-[#4A4A4A]">
                  <ShieldCheck className="w-5 h-5 text-[#1A4D2E]" />
                  <span className="text-sm">Vetted Subcontractors</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A4A4A]">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="text-sm">Performance Reviews</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A4A4A]">
                  <Users className="w-5 h-5 text-[#E07A5F]" />
                  <span className="text-sm">20+ Trade Categories</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758522487326-c9e158a9c44a?w=800&q=80"
                  alt="Happy customers"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 animate-slide-in stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Your Network</p>
                    <p className="text-sm text-[#71717A]">Trusted subs on demand</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 lg:py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-2">
                Browse by Trade
              </h2>
              <p className="text-[#4A4A4A]">Find subcontractors across all major trades</p>
            </div>
            <div className="flex items-center gap-4">
              {categories.length > 6 && (
                <Button
                  variant="outline"
                  onClick={() => setShowAllCategories(!showAllCategories)}
                  className="border-[#1A4D2E] text-[#1A4D2E]"
                  data-testid="toggle-categories-btn"
                >
                  {showAllCategories ? "Show Less" : `Show All ${categories.length}`}
                </Button>
              )}
              <Link
                to="/directory"
                className="flex items-center gap-2 text-[#1A4D2E] font-medium hover:underline"
                data-testid="view-all-categories"
              >
                View Directory
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-48 bg-stone-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(showAllCategories ? categories : categories.slice(0, 8)).map((category, index) => (
                <div
                  key={category.category_id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CategoryCard category={category} size="large" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Services */}
      {featuredServices.length > 0 && (
        <section className="py-16 lg:py-24 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-2">
                  Featured Services
                </h2>
                <p className="text-[#4A4A4A]">Top-rated providers in your area</p>
              </div>
              <Link
                to="/directory"
                className="flex items-center gap-2 text-[#1A4D2E] font-medium hover:underline"
                data-testid="view-all-services"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredServices.map((service, index) => (
                <div
                  key={service.service_id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ServiceCard service={service} featured />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 lg:py-24 px-6 lg:px-12 bg-[#1A4D2E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Are You a Subcontractor?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Get discovered by contractors and property managers in your area. 
            List your trade services and start getting work from verified professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Button
                asChild
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full px-8 py-6 text-lg btn-hover-lift"
              >
                <Link to="/create-listing" data-testid="cta-list-business">
                  List Your Trade
                </Link>
              </Button>
            ) : (
              <Button
                onClick={handleLogin}
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full px-8 py-6 text-lg btn-hover-lift"
                data-testid="cta-get-started"
              >
                Get Started Free
              </Button>
            )}
            <Button
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/directory" data-testid="cta-browse">
                Browse Subcontractors
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
